import { useState, useEffect } from 'react';
import axios from 'axios';
import { Gift, Heart, House, Airplane } from '@phosphor-icons/react';

const API = '/api';

// Default content fallback
const DEFAULT_REGISTRY = {
  message: "As we embark on this new chapter together, your love and presence mean more than any gift could express. However, if you wish to honor us with a gift, we've included an option below.",
  message_es: "Al comenzar este nuevo capítulo juntos, tu amor y presencia significan más de lo que cualquier regalo podría expresar. Sin embargo, si deseas honrarnos con un regalo, hemos incluido una opción a continuación.",
  venmo_usernames: ["@Stefa_Fernandez", "@Walter-Rodriguez-12"],
  venmo_note: "For your convenience, you can send monetary gifts through Venmo. Your generosity will help us build our life together.",
  venmo_note_es: "Para tu conveniencia, puedes enviar regalos monetarios a través de Venmo. Tu generosidad nos ayudará a construir nuestra vida juntos.",
  show_home_fund: true,
  show_honeymoon_fund: true
};

export default function RegistryPage() {
  const [content, setContent] = useState(DEFAULT_REGISTRY);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/registry`);
        if (response.data) {
          setContent(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('Using default registry content');
      }
    };
    fetchContent();
  }, []);

  return (
    <div data-testid="registry-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-1" style={{ color: "#B8A88A" }}>
            Your Generosity
          </p>
          <p className="font-body text-xs italic text-stone-400 mb-4">
            Tu Generosidad
          </p>
          <h1 className="page-title">Registry</h1>
          <p className="font-body text-sm italic text-stone-400 mb-2">Regalos</p>
          <p className="page-subtitle">
            Your presence at our wedding is the greatest gift of all
          </p>
          <p className="font-body text-xs italic text-stone-400">
            Tu presencia en nuestra boda es el mejor regalo de todos
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4">
          {/* Message */}
          <div className="text-center mb-16">
            <Heart size={48} weight="light" className="text-[#B8A88A] mx-auto mb-6" />
            <p className="font-body text-lg text-stone-600 leading-relaxed max-w-xl mx-auto mb-4">
              {content.message}
            </p>
            {content.message_es && (
              <p className="font-body text-sm italic text-stone-400 leading-relaxed max-w-xl mx-auto">
                {content.message_es}
              </p>
            )}
          </div>

          {/* Venmo Option */}
          <div data-testid="venmo-section" className="bg-white border border-stone-200 p-8 md:p-12 text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#3D95CE] text-white rounded-full mb-6">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.8 0c.8 1.3 1.2 2.7 1.2 4.3 0 3.4-2.9 7.8-5.3 10.9H9.5L6.9 1.2l5.4-.5 1.5 12c1.4-2.3 3.1-5.8 3.1-8.2 0-1.4-.2-2.4-.6-3.2L19.8 0z"/>
              </svg>
            </div>
            
            <h2 className="font-display text-2xl text-stone-900 mb-4">
              Venmo
            </h2>
            
            <p className="font-body text-stone-600 mb-4 max-w-md mx-auto">
              {content.venmo_note || "For your convenience, you can send monetary gifts through Venmo. Your generosity will help us build our life together."}
            </p>
            {content.venmo_note_es && (
              <p className="font-body text-sm italic text-stone-400 mb-8 max-w-md mx-auto">
                {content.venmo_note_es}
              </p>
            )}

            <div className="bg-stone-50 border border-stone-200 p-6 inline-block">
              <p className="font-body text-sm text-stone-500 mb-2">Venmo Usernames</p>
              {(content.venmo_usernames || []).map((username, idx) => (
                <p key={idx} className="font-display text-xl text-[#B8A88A] mb-1 last:mb-0">{username}</p>
              ))}
            </div>

            <p className="font-body text-xs text-stone-400 mt-6">
              Please include your name in the payment note so we can thank you properly!
            </p>
            <p className="font-body text-xs italic text-stone-400 mt-1">
              ¡Por favor incluye tu nombre en la nota del pago para que podamos agradecerte apropiadamente!
            </p>
          </div>

          {/* Alternative Ideas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-stone-200 p-6">
              <House size={32} weight="light" className="text-[#B8A88A] mb-4" />
              <h3 className="font-display text-lg text-stone-900 mb-1">
                Home Fund
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Fondo para el Hogar</p>
              <p className="font-body text-sm text-stone-600">
                Help us create our dream home together. Every contribution goes toward 
                building our nest.
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-2">
                Ayúdanos a crear nuestro hogar soñado juntos. Cada contribución va hacia la construcción de nuestro nido.
              </p>
            </div>

            <div className="bg-white border border-stone-200 p-6">
              <Airplane size={32} weight="light" className="text-[#B8A88A] mb-4" />
              <h3 className="font-display text-lg text-stone-900 mb-1">
                Honeymoon Fund
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-2">Fondo para la Luna de Miel</p>
              <p className="font-body text-sm text-stone-600">
                Support our first adventure as newlyweds. We're dreaming of exploring 
                new places together.
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-2">
                Apoya nuestra primera aventura como recién casados. Soñamos con explorar nuevos lugares juntos.
              </p>
            </div>
          </div>

          {/* Thank You Note */}
          <div className="mt-16 p-8 bg-[#C9B99A] text-center">
            <Gift size={32} weight="light" className="text-white/80 mx-auto mb-4" />
            <h3 className="font-display text-xl text-white mb-1">
              A Note of Gratitude
            </h3>
            <p className="font-body text-xs italic text-white/70 mb-3">Una Nota de Gratitud</p>
            <p className="font-body text-stone-200 text-sm leading-relaxed max-w-md mx-auto mb-2">
              Whether you're able to join us in El Salvador or celebrate with us from afar, 
              your love and support mean everything. We're so grateful to have you in our lives.
            </p>
            <p className="font-body text-xs italic text-white/70 leading-relaxed max-w-md mx-auto">
              Ya sea que puedas acompañarnos en El Salvador o celebrar con nosotros desde lejos, tu amor y apoyo significan todo. Estamos muy agradecidos de tenerte en nuestras vidas.
            </p>
            <p className="font-display text-lg italic mt-6 text-white">
              With love / Con amor, Stefanie & Walter
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
